import React, { useState } from "react";
import {
  Box, TextField, Button, Typography, Checkbox, FormControlLabel,
  InputAdornment, IconButton, Link, useMediaQuery, Alert, CircularProgress, Collapse, Tooltip,
} from "@mui/material";
import {
  Visibility, VisibilityOff, Info, PersonOutlined, LockOutlined, ArrowForward,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import { loginSuccess } from "../redux/slices/authSlice";
import { PostLogin } from "../Api/Api";
import { apiErrorMessage } from "../Api/http";
import brandLogo from "../images/Logo---Colour.png";
import loginBg from "../images/Login.png";

const B = "#8C72FB";
const BD = "#7B5EF0";
const BL = "#A994FF";
const BDeep = "#6A4EE0";
const F = "'Inter',system-ui,-apple-system,sans-serif";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.07 + 0.3, ease: "easeOut" } }),
};

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isMobile = useMediaQuery("(max-width:900px)");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const id = userName.trim();
    if (!id || !password) {
      setError("Enter your email and password.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.post(
        PostLogin,
        { userName: id, password },
        { headers: { "Content-Type": "application/json" }, timeout: 20000 },
      );

      const body = res.data;
      if (!body || body.error || !body.data?.token) {
        setError(body?.message || "Invalid email or password.");
        return;
      }

      dispatch(loginSuccess({ ...body.data, remember }));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, "Could not sign you in. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const stagger = { visible: { transition: { staggerChildren: 0.07, delayChildren: 0.25 } } };
  const cardAnim = {
    hidden: { opacity: 0, x: -50, scale: 0.96 },
    visible: { opacity: 1, x: 0, scale: 1, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
  };

  const inputSx = {
    mb: 2.5,
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px", fontFamily: F, fontSize: ".92rem", backgroundColor: "#F6F5FA",
      "& fieldset": { borderColor: "#E8E6F0", borderWidth: "1.5px" },
      "&:hover fieldset": { borderColor: B },
      "&.Mui-focused fieldset": { borderColor: B, borderWidth: "2px", boxShadow: `0 0 0 3px rgba(140,114,251,.08)` },
      "&.Mui-focused": { backgroundColor: "white" },
    },
    "& .MuiInputLabel-root": { fontFamily: F, color: "#9A94A8", fontSize: ".86rem", "&.Mui-focused": { color: B } },
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
    >
      <Box sx={{
        position: "relative", minHeight: "100vh", overflow: "hidden", background: "#E8E0FF",
        display: "flex", alignItems: "center", justifyContent: isMobile ? "center" : "flex-start",
        px: isMobile ? 2 : { md: 6, lg: 10 }, py: 4,
      }}>
        {/* Background artwork (blue source image shifted to the purple brand theme) */}
        <Box sx={{
          position: "absolute", inset: 0, zIndex: 0,
          backgroundImage: `url(${loginBg})`,
          backgroundSize: "cover", backgroundPosition: isMobile ? "70% center" : "center",
          backgroundRepeat: "no-repeat",
          filter: "hue-rotate(38deg) saturate(1.05)",
        }} />
        {/* Soft purple wash to tie the image into the theme */}
        <Box sx={{
          position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none",
          background: "linear-gradient(120deg, rgba(232,224,255,.35) 0%, rgba(140,114,251,.08) 55%, rgba(106,78,224,.10) 100%)",
        }} />

        <motion.div
          variants={cardAnim} initial="hidden" animate="visible"
          style={{
            position: "relative", zIndex: 5,
            width: "100%", maxWidth: 440, background: "rgba(255,255,255,.96)", borderRadius: "24px",
            padding: isMobile ? "32px 24px 24px" : "40px 38px 30px",
            boxShadow: "0 24px 80px rgba(106,78,224,.18), 0 4px 20px rgba(0,0,0,.05)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${B}, ${BL})` }} />

          <motion.div variants={stagger} initial="hidden" animate="visible">
            <motion.div variants={fadeUp} custom={0}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 2.5 }}>
                <Box component="img" src={brandLogo} alt="ARA HumanSync" sx={{ height: 44, width: "auto", objectFit: "contain" }} />
              </Box>
            </motion.div>

            <motion.div variants={fadeUp} custom={1}>
              <Typography sx={{ textAlign: "center", fontWeight: 800, fontSize: "1.6rem", fontFamily: F, color: "#1A1A2E", mb: 0.4, letterSpacing: "-.02em" }}>
                Welcome Back!
              </Typography>
            </motion.div>

            <motion.div variants={fadeUp} custom={2}>
              <Typography sx={{ textAlign: "center", fontSize: ".86rem", fontFamily: F, color: "#8E8E9A", mb: 3 }}>
                Sign in by entering the information below
              </Typography>
            </motion.div>

            <Box component="form" onSubmit={handleSubmit}>
              <Collapse in={Boolean(error)}>
                <Alert
                  severity="error"
                  onClose={() => setError("")}
                  sx={{ mb: 2, borderRadius: "12px", fontFamily: F, fontSize: ".82rem", alignItems: "center", "& .MuiAlert-message": { py: 0.3 } }}
                >
                  {error}
                </Alert>
              </Collapse>

              <motion.div variants={fadeUp} custom={3}>
                <TextField fullWidth label="Email" placeholder="you@company.com"
                  value={userName} onChange={(e) => setUserName(e.target.value)}
                  autoComplete="username" autoFocus disabled={loading}
                  error={Boolean(error) && !userName.trim()} sx={inputSx}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlined sx={{ fontSize: 20, color: "#B8B3C5" }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </motion.div>

              <motion.div variants={fadeUp} custom={4}>
                <TextField fullWidth label="Password" placeholder="••••••••••••" value={password}
                  onChange={(e) => setPassword(e.target.value)} type={showPw ? "text" : "password"}
                  autoComplete="current-password" disabled={loading}
                  error={Boolean(error) && !password}
                  sx={{ ...inputSx, mb: 1.5 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined sx={{ fontSize: 20, color: "#B8B3C5" }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip arrow title={showPw ? "Hide password" : "Show password"}>
                            <IconButton
                              onClick={() => setShowPw((v) => !v)}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                              disabled={loading}
                              aria-label={showPw ? "Hide password" : "Show password"}
                              aria-pressed={showPw}
                              sx={{
                                color: showPw ? B : "#B8B3C5",
                                transition: "color .18s",
                                "&:hover": { color: B, bgcolor: "rgba(140,114,251,.08)" },
                              }}
                            >
                              {showPw
                                ? <Visibility sx={{ fontSize: 20 }} />
                                : <VisibilityOff sx={{ fontSize: 20 }} />}
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </motion.div>

              <motion.div variants={fadeUp} custom={5}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
                  <FormControlLabel
                    control={<Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} disabled={loading}
                      sx={{ color: "#D0CCE0", "&.Mui-checked": { color: B }, "& .MuiSvgIcon-root": { fontSize: 20 } }} />}
                    label={<Typography sx={{ fontSize: ".82rem", fontFamily: F, color: "#6B6780", fontWeight: 500 }}>Remember Me</Typography>}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                    <Info sx={{ fontSize: 14, color: B, opacity: 0.7 }} />
                    <Link href="#" underline="none" sx={{ fontSize: ".82rem", fontFamily: F, fontWeight: 600, color: B, "&:hover": { opacity: 0.8 } }}>
                      Forgotten Password?
                    </Link>
                  </Box>
                </Box>
              </motion.div>

              <motion.div variants={fadeUp} custom={6}>
                <motion.div whileHover={loading ? undefined : { scale: 1.015, y: -1 }} whileTap={loading ? undefined : { scale: 0.985 }}>
                  <Button fullWidth type="submit" variant="contained" disabled={loading}
                    endIcon={loading
                      ? <CircularProgress size={18} thickness={5} sx={{ color: "#fff" }} />
                      : <ArrowForward sx={{ fontSize: 20 }} />}
                    sx={{
                      py: 1.5, borderRadius: "12px", fontSize: ".95rem", fontWeight: 700, fontFamily: F, textTransform: "none",
                      background: `linear-gradient(135deg, ${B}, ${BD})`, letterSpacing: ".01em", color: "#fff",
                      boxShadow: `0 6px 24px rgba(140,114,251,.35)`,
                      "&:hover": { background: `linear-gradient(135deg, ${BD}, ${BDeep})`, boxShadow: `0 10px 32px rgba(140,114,251,.45)` },
                      "&.Mui-disabled": { background: `linear-gradient(135deg, ${B}, ${BD})`, color: "#fff", opacity: .7 },
                    }}
                  >
                    {loading ? "Signing in…" : "Continue"}
                  </Button>
                </motion.div>
              </motion.div>
            </Box>

            <motion.div variants={fadeUp} custom={10}>
              <Typography sx={{ textAlign: "center", fontSize: ".72rem", fontFamily: F, color: "#C5C1D0", mt: 2.5 }}>
                &copy; 2025 ARA HumanSync. All rights reserved.
              </Typography>
            </motion.div>
          </motion.div>
        </motion.div>
      </Box>
    </motion.div>
  );
}
